// The editor's view of a CONTENT RECORD — shown under the header form of a
// transaction that has a content profile (`transaction.content`): the address
// the record will publish at, how it will look in a search result, and the
// "before you publish" checklist. Advisory only: nothing here blocks a save.
//
// Everything it shows is authored data (`content.editor_panel`, falling back to
// the defaults of the profile's kind) — title, icon, whether the preview shows,
// and which checks run with which limits. See ../contentPanel.ts.

import { useMemo, useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, Circle } from 'lucide-react'
import type { SqlModel } from '../types'
import { PageIcon } from '../../iconMap'
import {
  ContentFields, evaluateContentChecks, lengthBounds, resolveContentPanel,
  type ContentCheck, type ContentProfileLite,
} from '../contentPanel'

export type { ContentProfileLite } from '../contentPanel'

function slugify(v: unknown): string {
  return String(v ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80)
}

function meter(len: number, b: { min?: number; max?: number }): { color: string; note: string } {
  if (!len) return { color: 'var(--color-text-secondary)', note: 'empty' }
  if (b.min != null && len < b.min) return { color: '#d97706', note: `${len} — a little short` }
  if (b.max != null && len > b.max) return { color: '#d97706', note: `${len} — may be cut off` }
  if (b.min == null && b.max == null) return { color: 'var(--color-text-secondary)', note: String(len) }
  return { color: '#16a34a', note: `${len} — good` }
}

/** Bounds the preview's meter uses for a search field: the panel's own length
 *  check on it, when it has one that is switched on. */
function boundsFor(checks: ContentCheck[], field: string, fields: ContentFields): { min?: number; max?: number } {
  const c = checks.find(x => x.type === 'length' && x.field === field && x.enabled !== false)
  return c ? lengthBounds(c, fields.column(field)) : {}
}

export default function ContentPanel({ profile, sm, values }: {
  profile: ContentProfileLite
  sm?: SqlModel
  values: Record<string, unknown>
}) {
  const [open, setOpen] = useState(true)
  const panel = useMemo(() => resolveContentPanel(profile), [profile])

  const view = useMemo(() => {
    const fields = new ContentFields(profile, sm, values)
    const title = fields.plain('search.title')
    const summary = fields.plain('search.description')
    const slug = slugify(fields.raw('slug')) || slugify(fields.raw('title'))
    const base = String(profile.routes?.item || `${profile.routes?.index || 'items'}/:slug`)
    const path = `/${base.replace(':slug', slug || '…').replace(/^\/+/, '')}/`
    return {
      fields, title, summary, path,
      results: evaluateContentChecks(panel.checks, fields),
      titleBounds: boundsFor(panel.checks, 'search.title', fields),
      descBounds: boundsFor(panel.checks, 'search.description', fields),
    }
  }, [values, profile, sm, panel])

  // Publication state reads the visibility columns directly.
  const vis = profile.visibility || {}
  const colVal = (col?: string): unknown => {
    if (!col) return undefined
    const low = col.toLowerCase()
    return low in values ? values[low] : values[col]
  }
  const status = vis.status_column ? String(colVal(vis.status_column) ?? '') : ''
  const live = (vis.live_values || ['published']).map(x => x.toLowerCase())
  const isLive = !vis.status_column || live.includes(status.toLowerCase())
  const when = vis.schedule_column ? colVal(vis.schedule_column) : undefined
  const future = when ? new Date(String(when)).getTime() > Date.now() : false
  const noindex = ['y', 'yes', 'true', '1'].includes(String(view.fields.raw('seo.noindex') ?? '').toLowerCase())
  const state = noindex ? 'Hidden from search engines'
    : !isLive ? 'Not public — only records with a published status appear on the site'
      : future ? `Scheduled — goes live ${new Date(String(when)).toLocaleString()}`
        : 'Public — appears on the site at the next publish'

  const done = view.results.filter(r => r.ok).length
  const tm = meter(view.title.length, view.titleBounds)
  const dm = meter(view.summary.length, view.descBounds)
  const hasChecks = view.results.length > 0
  if (!panel.show_preview && !hasChecks) return null

  return (
    <div style={{
      margin: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 8,
      background: 'var(--color-bg-secondary)',
    }}>
      <button
        type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text)',
          fontSize: '0.8825rem', fontWeight: 600, textAlign: 'left',
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <PageIcon name={panel.icon} size={14} strokeWidth={2} /> {panel.title}
        {hasChecks && (
          <span style={{ marginLeft: 'auto', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
            {done}/{view.results.length} checks
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px', display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {panel.show_preview && (
            <div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Search result preview</div>
              <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 13, color: '#1a7f37', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.path}</div>
                <div style={{ fontSize: 18, color: '#1a0dab', lineHeight: 1.3, margin: '2px 0 4px' }}>
                  {view.title ? (view.titleBounds.max && view.title.length > view.titleBounds.max
                    ? `${view.title.slice(0, view.titleBounds.max - 3)}…` : view.title) : <em>No title yet</em>}
                </div>
                <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
                  {view.summary ? (view.descBounds.max && view.summary.length > view.descBounds.max
                    ? `${view.summary.slice(0, view.descBounds.max - 3)}…` : view.summary) : <em>Add a summary</em>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 13, marginTop: 6 }}>
                <span>Title: <span style={{ color: tm.color }}>{tm.note}</span></span>
                <span>Description: <span style={{ color: dm.color }}>{dm.note}</span></span>
              </div>
              <div style={{ fontSize: 13, marginTop: 8, color: isLive && !future && !noindex ? '#16a34a' : 'var(--color-text-secondary)' }}>
                {state}
              </div>
            </div>
          )}
          {hasChecks && (
            <div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>Before you publish</div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 5 }}>
                {view.results.map(r => (
                  <li key={r.check.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13.5 }}>
                    {r.ok
                      ? <CheckCircle2 size={14} style={{ color: '#16a34a', flexShrink: 0, marginTop: 1 }} />
                      : <Circle size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0, marginTop: 1 }} />}
                    <span style={{ color: r.ok ? 'var(--color-text)' : 'var(--color-text-secondary)' }}>{r.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
